import React, { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./auth";

import { ITask } from "src/interface/ITask";
import { IFollowupSchedule } from "src/interface/IFollowupSchedule";
import { getRoutes } from "src/utilities/routes";

// ==================== تعریف Interface ها ====================
interface TasksDataContextType {
  // داده‌های اصلی
  tasks: ITask[] | undefined;
  followupsSchedule: IFollowupSchedule[] | undefined;

  // وضعیت‌های loading
  isLoadingTasks: boolean;
  isLoadingFollowupsSchedule: boolean;

  // توابع refetch
  refetchTasks: () => void;
  refetchFollowupsSchedule: () => void;

  // خطاها
  errorTasks: any;
  errorFollowupsSchedule: any;
}

// ==================== ایجاد Context ====================
const TasksDataContext = createContext<TasksDataContextType | undefined>(
  undefined
);

// ==================== Provider Component ====================
interface TasksDataProviderProps {
  children: ReactNode;
}

export const TasksDataProvider: React.FC<TasksDataProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const Routes = getRoutes();

  // ==================== تابع fetch مشترک ====================
  const fetchData = React.useCallback(
    (url: string) =>
      fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      }).then((res) => res.json()),
    [user?.token]
  );

  // ==================== درخواست‌های API ====================
  const {
    data: tasks,
    isLoading: isLoadingTasks,
    error: errorTasks,
    refetch: refetchTasks,
  } = useQuery<ITask[]>({
    queryKey: ["TasksData"],
    queryFn: () => fetchData(Routes.TasksRoutes),
    staleTime: 2 * 60 * 1000, // 2 دقیقه
    gcTime: 5 * 60 * 1000, // 5 دقیقه
    enabled: !!user?.token, // فقط زمانی که token موجود است
  });

  const {
    data: followupsSchedule,
    isLoading: isLoadingFollowupsSchedule,
    error: errorFollowupsSchedule,
    refetch: refetchFollowupsSchedule,
  } = useQuery<IFollowupSchedule[]>({
    queryKey: ["FollowupsScheduleDataDashboard"],
    queryFn: () => fetchData(Routes.FollowupsScheduleRoutes),
    staleTime: 2 * 60 * 1000, // 2 دقیقه
    gcTime: 5 * 60 * 1000, // 5 دقیقه
    enabled: !!user?.token, // فقط زمانی که token موجود است
  });

  // ==================== مقداردهی Context ====================
  const value: TasksDataContextType = {
    tasks,
    followupsSchedule,
    isLoadingTasks,
    isLoadingFollowupsSchedule,
    refetchTasks,
    refetchFollowupsSchedule,
    errorTasks,
    errorFollowupsSchedule,
  };

  return (
    <TasksDataContext.Provider value={value}>
      {children}
    </TasksDataContext.Provider>
  );
};

// ==================== Hook برای استفاده از Context ====================
export const useTasksData = () => {
  const context = useContext(TasksDataContext);
  if (context === undefined) {
    throw new Error("useTasksData must be used within a TasksDataProvider");
  }
  return context;
};
